/**
 * /admin/clip-assignments — hand ball-clipping work out, and review it back.
 *
 * Pairs with the `clipping` app on the backend:
 *   GET  /api/clipping/review/assignments/[?status=]
 *   POST /api/clipping/review/assignments/
 *   GET  /api/clipping/review/assignments/<id>/
 *   GET  /api/clipping/review/assignments/<id>/stream/
 *   POST /api/clipping/review/assignments/<id>/approve/
 *   POST /api/clipping/review/assignments/bulk-approve/   {ids, note}
 *   POST /api/clipping/review/assignments/<id>/reject/
 *
 * The people doing the clipping never see this page — they work in the
 * Clip Workbench tab of the ingest app, which shows them one assignment
 * at a time and no catalog at all. This is the other end: who is doing
 * what, how far along they are, and the approve/reject decision.
 *
 * Approving is the only thing that writes BallVideo rows and fires the
 * slicer, so it is deliberately a two-step: open the assignment, watch
 * the cuts, then approve. A wrong approval costs real slicing.
 *
 * The drawer shows CLIPS, not the match. No clip file exists before
 * approval — slicing is what makes them — so it plays the master with
 * each cut's own boundaries imposed: no match timeline, no seeking
 * outside the delivery, the clock reading 0 at clip in, and the tape
 * covered until a ball is picked.
 *
 * Watching is not gated on the work being finished or handed in. Half a
 * match of clips is exactly what you want to look at — before the intern
 * spends another two hours repeating the same mistake — so the drawer
 * plays stamps at any status. It still writes nothing: approval remains
 * the only path to a BallVideo row, and still needs a submission.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  RefreshCw, Plus, CheckCircle2, XCircle, Clock, Scissors,
  AlertTriangle, ChevronRight, Users, Play, Pause, Square,
  SkipBack, SkipForward, RotateCcw, Trash2, Folder, FolderOpen,
} from "lucide-react";
import api from "../../api/axios";

const BASE = "/api/clipping/review/assignments/";
const FOLDERS_KEY = "clipFolders.open";
// The bulk-approve endpoint takes at most this many ids per request.
const BULK_APPROVE_BATCH = 20;
// MediaConvert at 540p: ~$0.62 for a ~240-clip match, measured
// 2026-10-06. Only for the estimate in the confirm dialog.
const USD_PER_CLIP = 0.0026;
// The Hundred is stored as 5-ball "overs" (sets), but is read in balls:
// over 0 is balls 1–5, over 19 is balls 96–100.
const HUNDRED_SET = 5;

// "Ball 37" on The Hundred, "6.2" everywhere else. The backend sends the
// label; the fallback covers rows from before it did.
const ballName = (b) => b.label || `${b.over}.${b.ball_in_over}`;

// An assignment's range, in the unit its format is read in.
function rangeText(r) {
  if (r.over_from == null) return "";
  if (r.ball_unit === "ball") {
    return ` · balls ${r.over_from * HUNDRED_SET + 1}–${(r.over_to + 1) * HUNDRED_SET}`;
  }
  return ` · ov ${r.over_from}–${r.over_to}`;
}

const STATUS_META = {
  ASSIGNED:    { label: "Assigned",    color: "text-text-secondary", icon: Clock },
  IN_PROGRESS: { label: "In progress", color: "text-amber-300",      icon: Scissors },
  SUBMITTED:   { label: "Submitted",   color: "text-blue-300",       icon: AlertTriangle },
  APPROVED:    { label: "Approved",    color: "text-emerald-300",    icon: CheckCircle2 },
  REJECTED:    { label: "Rejected",    color: "text-rose-300",       icon: XCircle },
};

// `status` goes to the API as a comma list. "In flight" is the default
// because unfinished work is worth looking at, not just work that has
// been handed in — and filtering finished rows out client-side would
// push the active ones through the API's 500-row cap.
const FILTERS = [
  { key: "ACTIVE", label: "In flight",
    status: "SUBMITTED,IN_PROGRESS,ASSIGNED,REJECTED" },
  { key: "SUBMITTED",   label: "Submitted",   status: "SUBMITTED" },
  { key: "IN_PROGRESS", label: "In progress", status: "IN_PROGRESS" },
  { key: "ASSIGNED",    label: "Assigned",    status: "ASSIGNED" },
  { key: "REJECTED",    label: "Rejected",    status: "REJECTED" },
  { key: "APPROVED",    label: "Approved",    status: "APPROVED" },
  { key: "", label: "All", status: "" },
];

export default function AdminClipAssignments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("ACTIVE");
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  // Ticked rows for bulk delete. Cleared on every reload so a tick can
  // never point at a row the current filter no longer shows.
  const [selected, setSelected] = useState(new Set());
  // Outcome of the last bulk approve: what went to the cutter and what
  // was left alone (with the reason), shown until the next one.
  const [bulkResult, setBulkResult] = useState(null);
  // One folder per clipper. Open folders are remembered per viewer so a
  // refresh or a filter change does not fold everything back up.
  const [openFolders, setOpenFolders] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(FOLDERS_KEY) || "[]"));
    } catch { return new Set(); }
  });
  const [clipperNames, setClipperNames] = useState({});

  useEffect(() => {
    api.get("/api/clipping/review/clippers/")
      .then(({ data }) => {
        const m = {};
        (data.clippers || []).forEach((c) => { if (c.name) m[c.email] = c.name; });
        setClipperNames(m);
      })
      .catch(() => {});      // folders fall back to the email
  }, []);

  function saveFolders(n) {
    setOpenFolders(n);
    try { localStorage.setItem(FOLDERS_KEY, JSON.stringify([...n])); } catch { /* private mode */ }
  }
  function toggleFolder(email) {
    const n = new Set(openFolders);
    if (n.has(email)) n.delete(email); else n.add(email);
    saveFolders(n);
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const spec = FILTERS.find((f) => f.key === filter);
      const { data } = await api.get(BASE, {
        params: spec && spec.status ? { status: spec.status } : {},
      });
      setRows(data.assignments || []);
      setSelected(new Set());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function openDetail(id) {
    setDetail({ loading: true, id });
    try {
      const { data } = await api.get(`${BASE}${id}/`);
      setDetail({ ...data, loading: false });
    } catch (e) {
      setDetail(null);
      setError(e?.response?.data?.detail || e.message);
    }
  }

  // Live view: while an unfinished assignment is open, pull its stamps
  // every 30 s so the reviewer sees clips as the intern cuts them.
  // Refreshed in place — the drawer is keyed by id, so the player, its
  // signed URL and the clip on screen all survive the update.
  const openId = detail && !detail.loading ? detail.id : null;
  const openStatus = detail?.status;
  useEffect(() => {
    if (!openId || !["ASSIGNED", "IN_PROGRESS", "REJECTED"].includes(openStatus)) {
      return undefined;
    }
    const timer = setInterval(() => {
      if (document.hidden) return;
      api.get(`${BASE}${openId}/`)
        .then(({ data }) => setDetail((d) => (
          d && d.id === openId && !d.loading ? { ...data, loading: false } : d
        )))
        .catch(() => {});   // a missed refresh is not worth an error banner
    }, 30000);
    return () => clearInterval(timer);
  }, [openId, openStatus]);

  async function decide(id, action, note) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${BASE}${id}/${action}/`, { note: note || "" });
      if (action === "approve") {
        // Keep the drawer open: as an approved assignment it shows the
        // slicing progress bar, which is the only way to know the clips
        // actually reached S3.
        await Promise.all([load(), openDetail(id)]);
      } else {
        setDetail(null);
        await load();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  // Approved rows cannot be deleted (their clips are cut and the row is
  // the record of who cut them), so they are never offered.
  const deletable = rows.filter((r) => r.status !== "APPROVED");

  function toggleRow(id) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function toggleFolderSelection(folderRows) {
    const ids = folderRows.filter((r) => r.status !== "APPROVED").map((r) => r.id);
    setSelected((s) => {
      const n = new Set(s);
      const all = ids.length > 0 && ids.every((id) => n.has(id));
      ids.forEach((id) => (all ? n.delete(id) : n.add(id)));
      return n;
    });
  }

  // Clipper folders, alphabetical by name; inside, matches in fixture
  // order ("Match 2" before "Match 10", hence the numeric collation).
  const folders = useMemo(() => {
    const by = new Map();
    rows.forEach((r) => {
      if (!by.has(r.assignee_email)) by.set(r.assignee_email, []);
      by.get(r.assignee_email).push(r);
    });
    const coll = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return [...by.entries()]
      .map(([email, list]) => ({
        email,
        name: clipperNames[email] || "",
        rows: [...list].sort((a, b) => coll.compare(a.label, b.label)),
      }))
      .sort((a, b) => coll.compare(a.name || a.email, b.name || b.email));
  }, [rows, clipperNames]);

  function toggleAll() {
    setSelected((s) => (s.size === deletable.length && deletable.length
      ? new Set() : new Set(deletable.map((r) => r.id))));
  }

  async function removeAssignments(ids) {
    if (!ids.length) return;
    const withWork = rows.filter((r) => ids.includes(r.id) && r.balls_stamped > 0);
    const msg = `Delete ${ids.length} assignment${ids.length === 1 ? "" : "s"}?`
      + (withWork.length
        ? `\n\n${withWork.length} of them already ha${withWork.length === 1 ? "s" : "ve"} clip`
          + " stamps from the clipper. Those stamps are deleted too."
        : "")
      + "\n\nThis cannot be undone. Any video the clipper uploaded stays in storage.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`${BASE}bulk-delete/`, { ids });
      setDetail(null);
      await load();
      if (data.refused && data.refused.length) setError(data.detail);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  // Ticked rows that can be approved right now. Other ticked rows are
  // still fine for bulk delete; approve simply leaves them out.
  const approvable = rows.filter(
    (r) => selected.has(r.id) && r.status === "SUBMITTED",
  );

  async function approveSelected() {
    const list = approvable;
    if (!list.length) return;
    const clips = list.reduce((n, r) => n + (r.balls_stamped || 0), 0);
    const msg = `Approve and cut ${list.length} assignment${list.length === 1 ? "" : "s"}`
      + ` (${clips.toLocaleString("en-IN")} clips)?`
      + `\n\nCutting costs about $${(clips * USD_PER_CLIP).toFixed(2)} in AWS MediaConvert.`
      + " The matches are cut one after another; each one's progress shows"
      + " in its drawer.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError(null);
    setBulkResult(null);
    const result = { approved: [], skipped: [], clips: 0 };
    try {
      const ids = list.map((r) => r.id);
      for (let i = 0; i < ids.length; i += BULK_APPROVE_BATCH) {
        const { data } = await api.post(`${BASE}bulk-approve/`, {
          ids: ids.slice(i, i + BULK_APPROVE_BATCH),
        });
        result.approved.push(...(data.approved || []));
        result.skipped.push(...(data.skipped || []));
        result.clips += data.clips_submitted || 0;
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBulkResult(result);
      setBusy(false);
      await load();
    }
  }

  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center gap-3 flex-wrap">
        <Scissors className="w-6 h-6 text-brand" />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold">Clip assignments</h1>
          <p className="text-sm text-text-secondary">
            Ball-clipping work handed to the team. Clippers set clip in and
            clip out in the ingest app; approving here is what actually cuts
            the clips.
          </p>
        </div>
        <button
          className="btn-secondary inline-flex items-center gap-2"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        {approvable.length > 0 && (
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={approveSelected}
            disabled={busy}
            title="Approve every ticked assignment that has been submitted, and cut its clips"
          >
            <CheckCircle2 className="w-4 h-4" />
            {busy ? "Working…" : `Approve & cut selected (${approvable.length})`}
          </button>
        )}
        {selected.size > 0 && (
          <button
            className="btn-danger inline-flex items-center gap-2"
            onClick={() => removeAssignments([...selected])}
            disabled={busy}
          >
            <Trash2 className="w-4 h-4" /> Delete selected ({selected.size})
          </button>
        )}
        <button
          className="btn-primary inline-flex items-center gap-2"
          onClick={() => setShowCreate(true)}
        >
          <Plus className="w-4 h-4" /> New assignment
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {bulkResult && (
        <div className="rounded-lg border border-border-default bg-bg-secondary/40 px-4 py-3 text-sm space-y-1.5">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
            <div className="flex-1">
              {bulkResult.approved.length
                ? `Approved ${bulkResult.approved.length} assignment${bulkResult.approved.length === 1 ? "" : "s"};`
                  + ` ${bulkResult.clips.toLocaleString("en-IN")} clips sent for cutting, one match after another.`
                : "Nothing was approved."}
            </div>
            <button
              className="text-text-secondary hover:text-text-primary"
              onClick={() => setBulkResult(null)}
              aria-label="Dismiss"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
          {bulkResult.skipped.length > 0 && (
            <div className="flex items-start gap-2 text-amber-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div>{bulkResult.skipped.length} left as they were:</div>
                <ul className="mt-1 space-y-1 text-text-secondary">
                  {bulkResult.skipped.map((r) => (
                    <li key={r.id}>
                      <span className="text-text-primary">{r.label || r.id}</span> — {r.detail}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => {
          // Counts come from the rows actually loaded, so they only mean
          // anything for what the current filter fetched.
          const n = STATUS_META[f.key] ? counts[f.key] : rows.length;
          return (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                filter === f.key
                  ? "border-brand text-brand bg-brand/10"
                  : "border-border text-text-secondary hover:border-border-strong"
              }`}
            >
              {f.label}{n ? ` · ${n}` : ""}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-text-secondary">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-text-secondary">
          <Users className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Nothing here.{" "}
          {filter === "ACTIVE"
            ? "No clipping work is in flight."
            : filter === "SUBMITTED"
              ? "No assignment is waiting on you."
              : "Try another filter, or create an assignment."}
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="flex items-center justify-end gap-3 border-b border-border bg-bg-secondary px-4 py-1.5 text-xs text-text-secondary">
            <button type="button" className="underline"
                    onClick={() => saveFolders(new Set(folders.map((f) => f.email)))}>
              Open all
            </button>
            <button type="button" className="underline"
                    onClick={() => saveFolders(new Set())}>
              Close all
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary text-xs uppercase tracking-wide text-text-secondary">
              <tr>
                <th className="w-10 px-4 py-2.5">
                  <input
                    type="checkbox" aria-label="Select all"
                    disabled={deletable.length === 0}
                    checked={deletable.length > 0 && selected.size === deletable.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="text-left px-4 py-2.5">Clipper / match</th>
                <th className="text-left px-4 py-2.5">Scope</th>
                <th className="text-left px-4 py-2.5">Progress</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            {folders.map((f) => {
              const isOpen = openFolders.has(f.email);
              const stamped = f.rows.reduce((n, r) => n + (r.balls_stamped || 0), 0);
              const total = f.rows.reduce((n, r) => n + (r.balls_total || 0), 0);
              const pct = total ? Math.round((100 * stamped) / total) : 0;
              const byStatus = {};
              f.rows.forEach((r) => { byStatus[r.status] = (byStatus[r.status] || 0) + 1; });
              const selectable = f.rows.filter((r) => r.status !== "APPROVED");
              const nSel = selectable.filter((r) => selected.has(r.id)).length;
              const FolderIcon = isOpen ? FolderOpen : Folder;
              return (
                <tbody key={f.email}>
                  <tr
                    className="border-t border-border bg-bg-secondary/40 hover:bg-bg-secondary cursor-pointer"
                    onClick={() => toggleFolder(f.email)}
                  >
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox" aria-label={`Select all for ${f.name || f.email}`}
                        disabled={selectable.length === 0}
                        checked={selectable.length > 0 && nSel === selectable.length}
                        ref={(el) => { if (el) el.indeterminate = nSel > 0 && nSel < selectable.length; }}
                        onChange={() => toggleFolderSelection(f.rows)}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <ChevronRight className={`w-4 h-4 text-text-secondary transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        <FolderIcon className="w-4 h-4 text-brand" />
                        <span className="font-semibold">{f.name || f.email}</span>
                        {f.name && <span className="text-xs text-text-secondary">{f.email}</span>}
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-text-secondary">
                          {f.rows.length} match{f.rows.length === 1 ? "" : "es"}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5" />
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 rounded-full bg-bg-secondary overflow-hidden">
                          <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-text-secondary tabular-nums">
                          {stamped}/{total}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className="flex flex-wrap gap-x-2">
                        {Object.keys(STATUS_META).filter((k) => byStatus[k]).map((k) => (
                          <span key={k} className={STATUS_META[k].color}>
                            {byStatus[k]} {STATUS_META[k].label.toLowerCase()}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-4 py-2.5" />
                  </tr>
                  {isOpen && f.rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.ASSIGNED;
                    const Icon = meta.icon;
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-border/60 hover:bg-bg-secondary/50 cursor-pointer"
                        onClick={() => openDetail(r.id)}
                      >
                        <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                          {r.status !== "APPROVED" && (
                            <input
                              type="checkbox" aria-label="Select"
                              checked={selected.has(r.id)}
                              onChange={() => toggleRow(r.id)}
                            />
                          )}
                        </td>
                        <td className="py-2 pl-14 pr-4 font-medium">{r.label}</td>
                        <td className="px-4 py-2 text-text-secondary">
                          {r.innings_number ? `Inns ${r.innings_number}` : "Both innings"}
                          {rangeText(r)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-24 rounded-full bg-bg-secondary overflow-hidden">
                              <div className="h-full bg-brand" style={{ width: `${r.pct_complete}%` }} />
                            </div>
                            <span className="text-xs text-text-secondary tabular-nums">
                              {r.balls_stamped}/{r.balls_total}
                            </span>
                          </div>
                        </td>
                        <td className={`px-4 py-2 ${meta.color}`}>
                          <span className="inline-flex items-center gap-1.5">
                            <Icon className="w-4 h-4" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-text-secondary">
                          <ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>
      )}

      {showCreate && (
        <CreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {detail && (
        <DetailDrawer
          key={detail.id}
          detail={detail}
          busy={busy}
          onClose={() => setDetail(null)}
          onDecide={decide}
          onDelete={(id) => removeAssignments([id])}
        />
      )}
    </div>
  );
}

/* ── Create ──────────────────────────────────────────────────────── */

function CreateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    assignee_id: "", innings_number: "",
    over_from: "", over_to: "", display_label: "",
  });
  // Picked matches, keyed by id, holding the row so a pick survives the
  // search moving on to other matches. Insertion order = pick order.
  const [picked, setPicked] = useState({});
  const pickedIds = Object.keys(picked);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Match picker. Searching server-side rather than filtering a full
  // list: there are tens of thousands of matches, most of them ingested.
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [video, setVideo] = useState(null);
  const [clippers, setClippers] = useState([]);

  useEffect(() => {
    api.get("/api/clipping/review/clippers/")
      .then(({ data }) => setClippers(data.clippers || []))
      .catch(() => setClippers([]));
  }, []);

  // Only the newest search may write the list. Typing "IPL 2025" fires
  // "IPL 202" and "IPL 2025" a beat apart, and the broader one (400
  // rows, seven seasons) is the slower query — it used to land last and
  // overwrite the right answer with M1–M60 repeated per season.
  const searchSeq = useRef(0);
  useEffect(() => {
    const seq = ++searchSeq.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/api/clipping/review/matches/", {
          params: search ? { search } : {},
        });
        if (seq === searchSeq.current) setMatches(data.matches || []);
      } catch { /* the form still works; the field just stays empty */ }
      finally { if (seq === searchSeq.current) setSearching(false); }
    }, 250);   // debounce: this fires on every keystroke otherwise
    return () => clearTimeout(t);
  }, [search]);

  // Check the master the moment a match is chosen, so a missing or
  // archived video is news now rather than a 409 after filling the form.
  // Only for a single pick: checking S3 for every match in a season
  // would be one listing per row, and a missing video no longer blocks
  // anything — the clipper uploads it.
  // Hundred-only picks take the range as balls 1–100; anything else (or
  // a mix) stays in overs. A range typed in one unit means nothing in
  // the other, so it is cleared when the unit flips.
  const hundredCount = pickedIds.filter((id) => picked[id].ball_unit === "ball").length;
  const inBalls = pickedIds.length > 0 && hundredCount === pickedIds.length;
  const mixedUnits = hundredCount > 0 && !inBalls;
  useEffect(() => {
    setForm((f) => ({ ...f, over_from: "", over_to: "" }));
  }, [inBalls]);

  const onlyId = pickedIds.length === 1 ? pickedIds[0] : "";
  useEffect(() => {
    if (!onlyId) { setVideo(null); return; }
    setVideo({ loading: true });
    api.get(`/api/clipping/review/matches/${onlyId}/video/`)
      .then(({ data }) => setVideo(data))
      .catch(() => setVideo(null));
  }, [onlyId]);

  function toggle(m) {
    setPicked((p) => {
      const next = { ...p };
      if (next[m.id]) delete next[m.id]; else next[m.id] = m;
      return next;
    });
  }
  function pickAllShown() {
    setPicked((p) => {
      const next = { ...p };
      matches.forEach((m) => { next[m.id] = m; });
      return next;
    });
  }
  const matchLabel = (m) =>
    `${m.label}${m.competition ? ` · ${m.competition}` : ""}`
    + `${m.match_date ? ` · ${m.match_date}` : ""}`;

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    // Blank means "no limit" on the backend, so strip empties rather
    // than sending "" and having the serializer reject an int field.
    if (pickedIds.length === 0) {
      setError("Pick at least one match.");
      setSaving(false);
      return;
    }
    const payload = { match_ids: pickedIds };
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v !== null) payload[k] = v;
    });
    if (inBalls) {
      // Balls → the 5-ball sets the backend stores, rounded outwards so
      // the typed balls are always covered.
      for (const k of ["over_from", "over_to"]) {
        if (payload[k] == null) continue;
        const n = Number(payload[k]);
        if (!Number.isInteger(n) || n < 1 || n > 100) {
          setError("Balls run from 1 to 100.");
          setSaving(false);
          return;
        }
        payload[k] = Math.floor((n - 1) / HUNDRED_SET);
      }
    }
    try {
      await api.post(BASE, payload);
      onCreated();
    } catch (e2) {
      setError(e2?.response?.data?.detail || JSON.stringify(e2?.response?.data || e2.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title="New clip assignment">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Matches" hint="Tick one or more. Each match becomes its own assignment for the same clipper. Ingested matches are included.">
          <input
            className="input mb-2"
            placeholder="Search matches…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="mb-1 flex items-center justify-between text-xs text-slate-400">
            <span>{pickedIds.length} selected</span>
            <span className="flex gap-3">
              <button type="button" className="underline disabled:opacity-40"
                      disabled={matches.length === 0} onClick={pickAllShown}>
                Select all shown
              </button>
              <button type="button" className="underline disabled:opacity-40"
                      disabled={pickedIds.length === 0} onClick={() => setPicked({})}>
                Clear
              </button>
            </span>
          </div>
          <div className="input h-48 overflow-y-auto p-0">
            {searching && <div className="px-3 py-2 text-slate-400">Searching…</div>}
            {!searching && matches.length === 0 && (
              <div className="px-3 py-2 text-slate-400">No matches found</div>
            )}
            {matches.map((m) => (
              <label key={m.id}
                     className="flex cursor-pointer items-start gap-2 px-3 py-1.5 hover:bg-white/5">
                <input type="checkbox" className="mt-0.5"
                       checked={!!picked[m.id]} onChange={() => toggle(m)} />
                <span>{matchLabel(m)}{` · ${m.source}`}</span>
              </label>
            ))}
          </div>
          {pickedIds.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {pickedIds.map((id) => (
                <span key={id}
                      className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-xs">
                  {picked[id].label}
                  <button type="button" aria-label="Remove"
                          onClick={() => toggle(picked[id])}>×</button>
                </span>
              ))}
            </div>
          )}
        </Field>

        {pickedIds.length > 1 && (
          <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
            {pickedIds.length} assignments will be created for this clipper. Any
            match without a video in storage is uploaded by the clipper from
            their computer.
          </div>
        )}

        {video && !video.loading && (
          video.present ? (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
              Master video found.
              {video.video_state && video.video_state.state !== "available"
                && video.video_state.state !== "restored"
                ? ` But it is ${video.video_state.state} in cold storage — the clipper
                   will not be able to play it until it is retrieved.`
                : ""}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              {video.detail || "No master video for this match."}
            </div>
          )
        )}

        <Field label="Clipper" hint="Your team and anyone holding the clipper grant. No user id needed.">
          <select className="input" required value={form.assignee_id}
                  onChange={(e) => set("assignee_id", e.target.value)}>
            <option value="">— pick someone —</option>
            {clippers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name ? `${c.name} · ` : ""}{c.email} · {c.kind}
                {c.open_assignments ? ` · ${c.open_assignments} open` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Label shown to the clipper"
               hint="Optional. Blank shows each match as it is, e.g. Match 1 · Chennai Super Kings vs Mumbai Indians. Typed text goes in front of each match's name.">
          <input className="input" placeholder="e.g. IPL 2025 (optional)"
                 value={form.display_label}
                 onChange={(e) => set("display_label", e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Innings" hint="Blank = both">
            <select className="input" value={form.innings_number}
                    onChange={(e) => set("innings_number", e.target.value)}>
              <option value="">Both</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </Field>
          {inBalls ? (
            <>
              <Field label="From ball" hint="1 is the first ball">
                <input className="input" type="number" min="1" max="100" value={form.over_from}
                       onChange={(e) => set("over_from", e.target.value)} />
              </Field>
              <Field label="To ball" hint="Inclusive, up to 100">
                <input className="input" type="number" min="1" max="100" value={form.over_to}
                       onChange={(e) => set("over_to", e.target.value)} />
              </Field>
            </>
          ) : (
            <>
              <Field label="From over" hint="0-based: 0 is the first over">
                <input className="input" type="number" min="0" value={form.over_from}
                       onChange={(e) => set("over_from", e.target.value)} />
              </Field>
              <Field label="To over" hint="Inclusive: 9 is the tenth over">
                <input className="input" type="number" min="0" value={form.over_to}
                       onChange={(e) => set("over_to", e.target.value)} />
              </Field>
            </>
          )}
        </div>
        {inBalls && (form.over_from !== "" || form.over_to !== "") && (
          <div className="text-xs text-text-secondary">
            Ranges cover whole 5-ball sets, so they round out to the nearest
            set: balls 3–12 become balls 1–15.
          </div>
        )}
        {mixedUnits && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            {hundredCount} of these are Hundred matches, so the range is in
            overs for all of them; on a Hundred match over N is balls
            5N+1 to 5N+5. Create the Hundred matches on their own to enter balls.
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Creating…"
              : pickedIds.length > 1 ? `Create ${pickedIds.length} assignments` : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Review ──────────────────────────────────────────────────────── */

function DetailDrawer({ detail, busy, onClose, onDecide, onDelete }) {
  const [note, setNote] = useState("");
  const [stream, setStream] = useState(null);      // {url} once resolved
  const [streamError, setStreamError] = useState(null);
  const [mediaError, setMediaError] = useState(null); // the <video>'s own failure
  const retriedRef = useRef(false);                  // one fresh URL per failure run
  const [activeId, setActiveId] = useState(null);  // the clip on screen
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);               // seconds INTO the clip
  const [chain, setChain] = useState(false);       // playing straight through
  const videoRef = useRef(null);
  const activeRef = useRef(null);                  // what the clamp reads
  const id = detail.id;

  // One presigned URL per opened drawer. It is short-lived by design —
  // prod signs with the EC2 instance role's rotating token — so the
  // reviewer gets a fresh one each time they open an assignment rather
  // than one that claims an hour and dies in four minutes.
  useEffect(() => {
    let cancelled = false;
    if (!id || detail.loading) return undefined;
    // No reset first: the drawer is keyed by assignment id where it is
    // rendered, so each one mounts with this state already null.
    api.get(`${BASE}${id}/stream/`)
      .then(({ data }) => { if (!cancelled) setStream(data); })
      .catch((e) => {
        if (!cancelled) setStreamError(e?.response?.data?.detail || e.message);
      });
    return () => { cancelled = true; };
  }, [id, detail.loading]);

  // The <video> fails silently otherwise: a black box that never plays.
  // The usual cause is the signed URL dying under an open drawer — prod
  // signs with the instance role's rotating token — so the first failure
  // fetches a fresh URL and resumes the clip; only a second one is shown.
  function onVideoError() {
    const v = videoRef.current;
    const err = v && v.error;
    const what = err
      ? `${["", "aborted", "network error", "cannot decode", "source not supported"][err.code] || "error " + err.code}${err.message ? ` (${err.message})` : ""}`
      : "unknown error";
    if (retriedRef.current) {
      setMediaError(what);
      setPlaying(false);
      return;
    }
    retriedRef.current = true;
    api.get(`${BASE}${id}/stream/`)
      .then(({ data }) => {
        setStream(data);
        const b = activeRef.current;
        if (!b || !v) return;
        // Wait for the new source before seeking back into the cut.
        v.addEventListener("loadedmetadata", () => {
          v.currentTime = b.start_ms / 1000;
          v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        }, { once: true });
      })
      .catch((e) => setMediaError(e?.response?.data?.detail || e.message || what));
  }

  if (detail.loading) {
    return <Modal onClose={onClose} title="Loading…"><div /></Modal>;
  }

  const balls = detail.balls || [];
  const stamped = balls.filter((b) => b.start_ms !== null);
  // Surface the outliers rather than making someone scan 120 rows: a
  // mis-dragged OUT is the failure mode worth catching before slicing.
  const lengths = stamped.map((b) => (b.end_ms - b.start_ms) / 1000);
  const avg = lengths.length
    ? (lengths.reduce((a, b) => a + b, 0) / lengths.length) : 0;
  const odd = stamped.filter((b) => {
    const s = (b.end_ms - b.start_ms) / 1000;
    return s < 4 || s > 60;
  });
  const canDecide = detail.status === "SUBMITTED";

  const active = stamped.find((b) => b.ball_id === activeId) || null;
  const clipLen = active ? (active.end_ms - active.start_ms) / 1000 : 0;

  /* ── The player is a CLIP player, not a match player ─────────────
     The reviewer asked to see the clips, not the tape they came from,
     and there is no clip file to serve: slicing only happens at
     approval. So this plays the master with the cut's own boundaries
     imposed on it — no native timeline, no seeking outside the cut,
     the clock reading 0 at clip in. Until a ball is picked the master
     is covered, so no frame of the wider match is on screen.

     The clamp in onTimeUpdate is what makes that true rather than
     merely tidy-looking: without it, any drift past the OUT point
     would roll on into the next delivery.                          */

  function openClip(b, chained) {
    if (!b || !stream) return;
    activeRef.current = b;
    setActiveId(b.ball_id);
    setChain(!!chained);
    setPos(0);
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = b.start_ms / 1000;
    // Autoplay is allowed: the click that got here is the gesture.
    v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }

  function togglePlay() {
    const v = videoRef.current;
    const b = activeRef.current;
    if (!v || !b) return;
    if (v.paused) {
      if (v.currentTime >= b.end_ms / 1000 - 0.05) v.currentTime = b.start_ms / 1000;
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  }

  function replay() {
    const v = videoRef.current;
    const b = activeRef.current;
    if (!v || !b) return;
    v.currentTime = b.start_ms / 1000;
    setPos(0);
    v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }

  function step(delta) {
    const i = stamped.findIndex((b) => b.ball_id === activeId);
    const next = stamped[(i < 0 ? 0 : i) + delta];
    if (next) openClip(next, false);
  }

  // Seek WITHIN the cut. The bar is the clip's length, not the match's,
  // so there is nowhere to drag to that is not part of this delivery.
  function seekInClip(e) {
    const v = videoRef.current;
    const b = activeRef.current;
    if (!v || !b) return;
    const r = e.currentTarget.getBoundingClientRect();
    const f = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
    const len = (b.end_ms - b.start_ms) / 1000;
    v.currentTime = b.start_ms / 1000 + f * len;
    setPos(f * len);
  }

  function onTimeUpdate() {
    const v = videoRef.current;
    const b = activeRef.current;
    if (!v || !b) return;
    const start = b.start_ms / 1000;
    const end = b.end_ms / 1000;
    if (v.currentTime < start) { v.currentTime = start; return; }
    if (v.currentTime >= end) {
      v.pause();
      setPlaying(false);
      setPos(end - start);
      if (chain) {
        const i = stamped.findIndex((x) => x.ball_id === b.ball_id);
        const next = stamped[i + 1];
        if (next) openClip(next, true);
        else setChain(false);
      }
      return;
    }
    setPos(v.currentTime - start);
  }

  return (
    <Modal onClose={onClose} title={detail.label} wide>
      <div className="space-y-4 text-sm">
        <div className="flex gap-6 flex-wrap text-text-secondary">
          <span>Clipper <b className="text-text-primary">{detail.assignee_email}</b></span>
          <span>Stamped <b className="text-text-primary">{detail.balls_stamped}/{detail.balls_total}</b></span>
          <span>Avg length <b className="text-text-primary">{avg.toFixed(1)}s</b></span>
          <span>Status <b className="text-text-primary">{detail.status}</b></span>
        </div>

        {/* ── Clip player ────────────────────────────────────────── */}
        {streamError ? (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-rose-200">
            Cannot play the clips: {streamError}
            <span className="block text-xs opacity-80 mt-1">
              The stamp times below still read fine — you just cannot watch
              the cuts.
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative rounded-lg border border-border bg-black overflow-hidden">
              <video
                ref={videoRef}
                preload="metadata"
                playsInline
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={onTimeUpdate}
                onError={onVideoError}
                onPlaying={() => { retriedRef.current = false; setMediaError(null); }}
                onClick={togglePlay}
                src={stream ? stream.url : undefined}
                className="w-full max-h-[46vh] block cursor-pointer"
              />

              {/* Nothing picked yet: keep the wider match off screen. */}
              {!active && (
                <div className="absolute inset-0 bg-bg-primary flex items-center justify-center text-center px-6">
                  <span className="text-text-secondary text-xs">
                    {stream
                      ? (stamped.length
                          ? "Pick a ball below to watch its clip."
                          : "Nothing has been clipped yet.")
                      : "Loading…"}
                  </span>
                </div>
              )}

              {active && (
                <span className="absolute top-2 left-2 rounded bg-black/70 px-2 py-0.5 text-xs tabular-nums">
                  Inns {active.innings} · {ballName(active)}
                  <span className="text-text-secondary"> · {active.event}</span>
                </span>
              )}
            </div>

            {mediaError && (
              <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-rose-200 text-xs">
                The video would not load: {mediaError}
              </div>
            )}

            {/* Clip-scoped transport. No match timeline anywhere. */}
            <div className="flex items-center gap-2">
              <button
                className="btn-secondary px-2 py-1"
                title="Previous clip"
                disabled={!active}
                onClick={() => step(-1)}
              >
                <SkipBack className="w-3.5 h-3.5" />
              </button>
              <button
                className="btn-secondary px-2 py-1"
                title={playing ? "Pause" : "Play"}
                disabled={!active}
                onClick={togglePlay}
              >
                {playing
                  ? <Pause className="w-3.5 h-3.5" />
                  : <Play className="w-3.5 h-3.5" />}
              </button>
              <button
                className="btn-secondary px-2 py-1"
                title="Replay this clip"
                disabled={!active}
                onClick={replay}
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button
                className="btn-secondary px-2 py-1"
                title="Next clip"
                disabled={!active}
                onClick={() => step(1)}
              >
                <SkipForward className="w-3.5 h-3.5" />
              </button>

              <div
                className={`flex-1 h-1.5 rounded-full bg-bg-secondary overflow-hidden ${
                  active ? "cursor-pointer" : ""
                }`}
                onClick={active ? seekInClip : undefined}
              >
                <div
                  className="h-full bg-brand"
                  style={{ width: `${clipLen ? Math.min(100, (pos / clipLen) * 100) : 0}%` }}
                />
              </div>
              <span className="text-xs text-text-secondary tabular-nums w-20 text-right">
                {active ? `${pos.toFixed(1)} / ${clipLen.toFixed(1)}s` : "—"}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="btn-secondary inline-flex items-center gap-1.5 text-xs"
                disabled={!stream || stamped.length === 0}
                onClick={() => openClip(stamped[0], true)}
              >
                <Play className="w-3.5 h-3.5" />
                Play all {stamped.length} clips
              </button>
              {chain && (
                <button
                  className="btn-secondary inline-flex items-center gap-1.5 text-xs"
                  onClick={() => setChain(false)}
                >
                  <Square className="w-3.5 h-3.5" /> Stop after this one
                </button>
              )}
              <span className="text-xs text-text-secondary">
                Each clip plays from its clip in and stops at its clip out.
              </span>
            </div>
          </div>
        )}

        {odd.length > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200">
            <b>{odd.length}</b> clip{odd.length === 1 ? " is" : "s are"} under 4s or
            over 60s — usually a mis-drag. Check before approving:{" "}
            {odd.slice(0, 8).map(ballName).join(", ")}
            {odd.length > 8 ? "…" : ""}
          </div>
        )}

        <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
          <table className="w-full text-xs">
            <thead className="bg-bg-secondary sticky top-0 text-text-secondary">
              <tr>
                <th className="px-3 py-2 w-8" />
                <th className="text-left px-3 py-2">Ball</th>
                <th className="text-left px-3 py-2">Event</th>
                <th className="text-left px-3 py-2">Length</th>
                <th className="text-left px-3 py-2">Re-cuts</th>
              </tr>
            </thead>
            <tbody>
              {balls.map((b) => {
                const len = b.start_ms !== null ? (b.end_ms - b.start_ms) / 1000 : null;
                const playable = b.start_ms !== null && !!stream;
                const isActive = activeId === b.ball_id;
                return (
                  <tr
                    key={b.ball_id}
                    onClick={() => { if (playable) openClip(b, false); }}
                    className={`border-t border-border ${isActive ? "bg-brand/10" : ""} ${
                      playable ? "cursor-pointer hover:bg-bg-secondary/50" : ""
                    }`}
                  >
                    <td className="px-3 py-1.5 text-text-secondary">
                      {playable
                        ? <Play className={`w-3.5 h-3.5 ${isActive ? "text-brand" : ""}`} />
                        : null}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {b.innings} · {ballName(b)}
                    </td>
                    <td className="px-3 py-1.5 text-text-secondary">{b.event}</td>
                    <td className={`px-3 py-1.5 tabular-nums ${
                      len !== null && (len < 4 || len > 60) ? "text-amber-300" : ""
                    }`}>
                      {len !== null ? `${len.toFixed(1)}s` : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-text-secondary tabular-nums">
                      {b.revision > 1 ? b.revision : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {canDecide ? (
          <>
            <textarea
              className="input w-full" rows={2}
              placeholder="Note for the clipper (required when sending back)"
              value={note} onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                className="btn-secondary mr-auto inline-flex items-center gap-2"
                disabled={busy}
                onClick={() => onDelete(detail.id)}
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button className="btn-secondary" onClick={onClose}>Close</button>
              <button
                className="btn-danger"
                disabled={busy || !note.trim()}
                title={!note.trim() ? "Say what needs fixing" : ""}
                onClick={() => onDecide(detail.id, "reject", note)}
              >
                Send back
              </button>
              <button
                className="btn-primary"
                disabled={busy}
                onClick={() => onDecide(detail.id, "approve", note)}
              >
                {busy ? "Working…" : `Approve & cut ${detail.balls_stamped} clips`}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
          {detail.status === "APPROVED" && <SlicingProgress id={detail.id} />}
          <div className="flex justify-between items-center gap-4">
            <span className="text-text-secondary">
              {detail.status === "APPROVED"
                ? "Approved."
                : `Still with ${detail.assignee_email}. Watch as much as you like — there is nothing to approve until they submit.`}
            </span>
            <span className="flex gap-2">
              {detail.status !== "APPROVED" && (
                <button
                  className="btn-secondary inline-flex items-center gap-2"
                  disabled={busy}
                  onClick={() => onDelete(detail.id)}
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              )}
              <button className="btn-secondary" onClick={onClose}>Close</button>
            </span>
          </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ── Slicing progress ─────────────────────────────────────────────
   Approval only queues the cuts; MediaConvert makes the files and its
   callback flips each clip to ready. This polls the counts until
   nothing is left processing, so "approved" is never mistaken for
   "the clips are in S3".                                            */

const SLICING_POLL_MS = 4000;

function SlicingProgress({ id }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function tick() {
      if (!document.hidden) {
        try {
          const { data } = await api.get(`${BASE}${id}/slicing/`);
          if (cancelled) return;
          setP(data);
          setError(null);
          if (data.done || data.stuck) return;   // nothing more will change
        } catch (e) {
          if (cancelled) return;
          setError(e?.response?.data?.detail || e.message);
        }
      }
      timer = setTimeout(tick, SLICING_POLL_MS);
    }
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [id]);

  if (!p) {
    return (
      <div className="text-xs text-text-secondary">
        {error ? `Could not read slicing progress: ${error}` : "Checking the clips…"}
      </div>
    );
  }
  if (p.total === 0) {
    return (
      <div className="text-xs text-text-secondary">
        {p.stamped
          ? `None of this assignment's ${p.stamped} clip${p.stamped === 1 ? " is" : "s are"} on record any more — they were replaced or removed after approval.`
          : "Nothing was stamped, so no clips were cut."}
      </div>
    );
  }

  const pct = (n) => `${(100 * n) / p.total}%`;
  const tone = p.failed
    ? "border-rose-500/40 bg-rose-500/10"
    : p.done
      ? "border-emerald-500/40 bg-emerald-500/10"
      : p.stuck
        ? "border-amber-500/40 bg-amber-500/10"
        : "border-border bg-bg-secondary/40";

  return (
    <div className={`rounded-lg border px-3 py-2.5 space-y-2 ${tone}`}>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-medium text-text-primary">
          {p.done
            ? (p.failed
                ? `Finished — ${p.ready} of ${p.total} clips uploaded to AWS, ${p.failed} failed`
                : `All ${p.total} clips uploaded to AWS`)
            : `Cutting clips… ${p.ready} of ${p.total} uploaded to AWS`}
        </span>
        <span className="tabular-nums text-text-secondary">
          {Math.round((100 * (p.ready + p.failed)) / p.total)}%
        </span>
      </div>

      <div className="h-2 rounded-full bg-bg-secondary overflow-hidden flex">
        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: pct(p.ready) }} />
        <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: pct(p.failed) }} />
      </div>

      <div className="flex gap-4 flex-wrap text-xs text-text-secondary tabular-nums">
        <span><b className="text-emerald-300">{p.ready}</b> ready</span>
        <span><b className="text-text-primary">{p.processing}</b> processing</span>
        <span><b className={p.failed ? "text-rose-300" : "text-text-primary"}>{p.failed}</b> failed</span>
        {!p.done && !p.stuck && (
          <span className="inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3 animate-spin" /> updating live
          </span>
        )}
      </div>

      {p.stuck && (
        <div className="text-xs text-amber-200">
          {p.processing} clip{p.processing === 1 ? " has" : "s have"} been processing
          for over 30 minutes since approval — they have probably stalled.
          Ask for the slicing to be checked for this match.
        </div>
      )}

      {p.failures.length > 0 && (
        <div className="text-xs text-rose-200">
          Failed:{" "}
          {p.failures.map((f) => `${f.innings} · ${ballName(f)}`).join(", ")}
          {p.failed > p.failures.length ? "…" : ""}
          {p.failures[0].error && (
            <span className="block opacity-80 mt-0.5">
              First error: {p.failures[0].error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Bits ────────────────────────────────────────────────────────── */

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-text-secondary">{label}</span>
      {children}
      {hint && <span className="block text-xs text-text-secondary mt-0.5">{hint}</span>}
    </label>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-bg-primary border border-border rounded-xl p-5 w-full ${
          wide ? "max-w-3xl" : "max-w-lg"
        } max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
